import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Grid,
  Divider,
  Chip,
  Box,
  Avatar,
  LinearProgress,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { auditLogService } from '..//../Services/AuditLogService';
import { CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';



 
const KYCDetailsPopup = ({ open, onClose, log, onUpdate, onActionComplete }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const sessionData = log?.rawData || log;
  const digilockerData = sessionData?.kyc_sessions?.[0]?.digilocker_verification?.result;
  const panData = sessionData?.kyc_sessions?.[0]?.pan_verification;
  const livelinessData = sessionData?.kyc_sessions?.[0]?.liveliness_verification;
  const mediaData = sessionData?.kyc_sessions?.[0]?.media_storage;
  const { user } = useSelector((state) => state.auth);
 
  // Extract user information with fallbacks
  const userName = digilockerData?.pan_name ||
                   panData?.result?.detectedDetails?.[0]?.name ||
                   sessionData?.name_enc ||
                   "Unknown User";
 
  const userContact = digilockerData?.aadhaar_number ||
                     panData?.mobile ||
                     sessionData?.phone_enc ||
                     "N/A";
 
  const panNumber = digilockerData?.pan_number ||
                   panData?.result?.detectedDetails?.[0]?.panCardNumber ||
                   "N/A";
 
  const dateOfBirth = digilockerData?.pan_dob ||
                     panData?.result?.detectedDetails?.[0]?.dateOfBirth ||
                     "N/A";
 
  const createdAt = sessionData?.createdAt || sessionData?.updatedAt || new Date().toISOString();
 
  // Extract verification statuses
  const digilockerStatus = sessionData?.kyc_sessions?.[0]?.digilocker_verification?.status || "pending";
  const panStatus = sessionData?.kyc_sessions?.[0]?.pan_verification?.status || "pending";
  const livenessStatus = sessionData?.kyc_sessions?.[0]?.liveliness_verification?.status || "pending";
 
  // Extract liveliness verification results
  const livenessResult = livelinessData?.result;
  const silentAnalysis = livenessResult?.silentLivelinessAnalysis;
  const behavioralAnalysis = livenessResult?.behaviouralAnalysis;
  const derivedResults = livenessResult?.derived;
  
  // Extract scores and metrics
  const averageFakeScore = silentAnalysis?.averageFakeScore;
  const averageRealScore = silentAnalysis?.averageRealScore;
  const finalLivelinessScore = silentAnalysis?.finalLivelinessScore;
  const fakeImageCount = silentAnalysis?.fakeImageCount;
  const realImageCount = silentAnalysis?.realImageCount;
  const totalFrames = derivedResults?.totalFrames;
  const realRate = derivedResults?.realRate;
  const fakeRate = derivedResults?.fakeRate;
  const normalizedConfidence = derivedResults?.normalizedConfidence;
  const compositeConfidence = derivedResults?.compositeConfidence;
  
  // Extract behavioral detection results
  const headMovement = behavioralAnalysis?.headMovement === 1;
  const blink = behavioralAnalysis?.blink === 1;
  const smile = behavioralAnalysis?.smile === 1;
  const depth = behavioralAnalysis?.depth === 1;
  const behavioralScore = behavioralAnalysis?.score;
  
  // Overall classification
  const overallClassification = derivedResults?.overallClassification || silentAnalysis?.classification || "Unknown";

 
  const handleAccept = async (currentUser) => {
    setLoading(true);
    setError('');
    
    try {
  
      // Update KYC applicant auditor status
      await auditLogService.auditorDecision(log?.applicant_id, 'approved', currentUser);
      
      onActionComplete?.(
        "KYC session approved successfully. Notification email sent to the customer.",
        "success"
      );
      
      onUpdate && onUpdate();
      onClose();
    } catch (err) {
      setError('Failed to approve session');
      onActionComplete?.("Failed to approve KYC session. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async (currentUser) => {
    setLoading(true);
    setError('');
    
    try {
      // Update KYC applicant auditor status
      await auditLogService.auditorDecision(log?.applicant_id, 'rejected', currentUser);
      
      onActionComplete?.(
        "KYC session declined successfully. Notification email sent to the customer.",
        "success"
      );
      
      onUpdate && onUpdate();
      onClose();
    } catch (err) {
      setError('Failed to reject session');
      onActionComplete?.("Failed to decline KYC session. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
          borderBottom: "1px solid #e0e0e0",
          pb: 2,
          bgcolor: "#f8f9fa"
        }}
      >
        {/* Title */}
        <Typography
          variant="h5"
          fontWeight="bold"
          color="#1976d2"
          sx={{
            fontSize: { xs: "1.1rem", sm: "1.5rem" }, 
            textAlign: { xs: "center", sm: "left" },
            width: "100%"
          }}
        >
          KYC Details - {log?.applicant_id || "N/A"}
        </Typography>

        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            color: "#666",
            alignSelf: { xs: "center", sm: "auto" }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, bgcolor: "#fafafa" }}>
        {/* Profile Section */}
        <Box
          sx={{
            bgcolor: "white",
            p: 3,
            borderRadius: 3,
            mb: 3,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",

            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",

            flexDirection: {
              xs: "column",   // mobile
              sm: "column",   // small tablets
              md: "row"       // desktop (unchanged)
            },
            gap: {
              xs: 2,
              sm: 2,
              md: 0
            },
            textAlign: { xs: "center", md: "left" }
          }}
        >
          {/* Left: Avatar + Name */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: { xs: "column", md: "row" },
              textAlign: { xs: "center", md: "left" }
            }}
          >
            <Avatar
              sx={{
                bgcolor: "#1976d2",
                width: 56,
                height: 56,
                fontSize: "1.5rem",
                fontWeight: "bold",
                mr: { md: 2, xs: 0 },
                mb: { xs: 1, md: 0 }
              }}
            >
              {userName.charAt(0).toUpperCase()}
            </Avatar>

            <Box>
              <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1 }}>
                {userName}
              </Typography>

              <Typography
                color="text.secondary"
                variant="body2"
                sx={{ lineHeight: 1, mt: 0.5 }}
              >
                {userContact.substring(0, 8).replace(/[a-zA-Z0-9]/g, "x") +
                  userContact.substring(userContact.length - 4)}
              </Typography>
            </Box>
          </Box>

          {/* Center: Session Details */}
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              justifyContent: { xs: "center", md: "space-between" },
              alignItems: "center",
              mx: { md: 4, xs: 0 },
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 2, md: 0 },
              mt: { xs: 2, md: 0 }
            }}
          >
            {/* Session Details */}
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom align="center">
                Session Details
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "center", md: "flex-start" },
                  fontWeight: 500
                }}
              >
                <CalendarTodayIcon fontSize="small" sx={{ mr: 1, color: "#666" }} />
                {new Date(createdAt)
                  .toLocaleString("en-US", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric"
                  })
                  .replace(/\//g, "-")}
                ,{" "}
                {new Date(createdAt).toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false
                })}
              </Typography>
            </Box>

            {/* PAN */}
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom align="center">
                PAN
              </Typography>
              <Typography variant="body1" fontWeight="500">
                {panNumber}
              </Typography>
            </Box>

            {/* DOB */}
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom align="center">
                DOB
              </Typography>
              <Typography variant="body1" fontWeight="500">
                {dateOfBirth}
              </Typography>
            </Box>
          
          {/* Right: Status Chip */}
          <Box sx={{ textAlign: "center", mt: { xs: 2, md: 0 } }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Auditor Status
            </Typography>

            <Chip
              label={
                sessionData?.auditor_status === "approved"
                  ? "Approved"
                  : sessionData?.auditor_status === "rejected"
                  ? "Rejected"
                  : "Pending"
              }
              color={
                sessionData?.auditor_status === "approved"
                  ? "success"
                  : sessionData?.auditor_status === "rejected"
                  ? "error"
                  : "warning"
              }
              size="medium"
              sx={{
                fontWeight: "bold",
                fontSize: "1rem",
                px: 2,
                py: 0,
              }}
            />
          </Box>
        </Box>
      </Box>

        {/* Verification Results */}
        <Box sx={{ bgcolor: "white", p: 3, borderRadius: 3, mb: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3, color: "#1976d2" }}>
    Verification Results
  </Typography>

  <Grid container spacing={3}>
    {[
      { label: "DigiLocker Verification", status: digilockerStatus },
      { label: "PAN Verification", status: panStatus },
      { label: "Liveliness Check", status: livenessStatus }
    ].map((item) => (
      <Grid item xs={12} sm={6} md={4} key={item.label} display="flex">
        <Box
          sx={{
            p: 3,
            border: item.status === "success" ? "2px solid #e8f5e8" : "2px solid #fff3e0",
            borderRadius: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: item.status === "success" ? "#f8fff8" : "#fffbf0",
            transition: "all 0.3s ease",
            width: "100%",        // full width inside Grid item
            minWidth: 210,        // ensures boxes don’t shrink too much
            "&:hover": {
              boxShadow: item.status === "success" ? "0 4px 12px rgba(76,175,80,0.2)" : "0 4px 12px rgba(255,152,0,0.2)",
              transform: "translateY(-2px)"
            }
          }}
        >
          <Typography fontWeight="600" color={item.status === "success" ? "#2e7d32" : "#f57c00"}>
            {item.label}
          </Typography>
          {item.status === "success" ? (
            <CheckCircleIcon color="success" sx={{ fontSize: 28 }} />
          ) : (
            <WarningIcon color="warning" sx={{ fontSize: 28 }} />
          )}
        </Box>
      </Grid>
    ))}
  </Grid>
</Box>


 
        {/* Face Match & Liveness */}
        <Box sx={{
          bgcolor: "white",
          p: 3,
          borderRadius: 3,
          mb: 3,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
        }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom color="#1976d2">
              Face Match & Liveness Analysis
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Classification: {overallClassification}
              {compositeConfidence !== undefined && ` • Confidence: ${(compositeConfidence * 100).toFixed(1)}%`}
            </Typography>
            <Chip
              label={overallClassification === "Real" ? "PASS" : overallClassification === "Fake" ? "FAIL" : "REVIEW"}
              color={overallClassification === "Real" ? "success" : overallClassification === "Fake" ? "error" : "warning"}
              size="large"
              sx={{
                fontWeight: "bold",
                fontSize: "1rem",
                px: 2
              }}
            />
          </Box>
 
          <Grid container spacing={3}>
            {[
              ...(compositeConfidence !== undefined ? [{ label: "Composite Confidence", value: compositeConfidence, color: "#2196f3" }] : []),
              ...(normalizedConfidence !== undefined ? [{ label: "Normalized Confidence", value: normalizedConfidence, color: "#9c27b0" }] : []),
              ...(averageFakeScore !== undefined ? [{ label: "Average Fake Score", value: averageFakeScore, color: "#f44336" }] : []),
              ...(averageRealScore !== undefined ? [{ label: "Average Real Score", value: averageRealScore, color: "#4caf50" }] : []),
              ...(behavioralScore !== undefined ? [{ label: "Behavioral Score", value: behavioralScore, color: "#ff9800" }] : [])
            ].map((item) => (
              <Grid item xs={12} md={6} key={item.label}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" fontWeight="600" gutterBottom color="#333">
                    {item.label}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, Math.abs(item.value) * 100)}
                    sx={{
                      borderRadius: 3,
                      height: 12,
                      bgcolor: "#f0f0f0",
                      mb: 1,
                      "& .MuiLinearProgress-bar": {
                        bgcolor: item.color,
                        borderRadius: 3
                      }
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" fontWeight="500">
                    {item.value.toFixed(3)}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
 
        {/* Behavioral Detection Results */}
        <Box sx={{
          bgcolor: "white",
          p: 3,
          borderRadius: 3,
          mb: 3,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
        }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom color="#1976d2" sx={{ mb: 2 }}>
            Behavioral Detection Results
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 3 }}>
            <Chip
              label={`Head Movement: ${headMovement ? 'Detected' : 'Not Detected'}`}
              color={headMovement ? "success" : "warning"}
              sx={{ fontWeight: "500", fontSize: "0.875rem" }}
            />
            <Chip
              label={`Blink: ${blink ? 'Detected' : 'Not Detected'}`}
              color={blink ? "success" : "warning"}
              sx={{ fontWeight: "500", fontSize: "0.875rem" }}
            />
            <Chip
              label={`Smile: ${smile ? 'Detected' : 'Not Detected'}`}
              color={smile ? "success" : "warning"}
              sx={{ fontWeight: "500", fontSize: "0.875rem" }}
            />
            <Chip
              label={`Depth: ${depth ? 'Detected' : 'Not Detected'}`}
              color={depth ? "success" : "warning"}
              sx={{ fontWeight: "500", fontSize: "0.875rem" }}
            />
          </Box>
          
          <Typography variant="h6" fontWeight="bold" gutterBottom color="#1976d2" sx={{ mb: 2, mt: 3 }}>
            Frame Analysis Summary
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 3 }}>
            {totalFrames !== undefined && (
              <Chip
                label={`Total Frames: ${totalFrames}`}
                color="info"
                sx={{ fontWeight: "500", fontSize: "0.875rem" }}
              />
            )}
            {realImageCount !== undefined && (
              <Chip
                label={`Real Images: ${realImageCount}`}
                color={realImageCount > 0 ? "success" : "warning"}
                sx={{ fontWeight: "500", fontSize: "0.875rem" }}
              />
            )}
            {fakeImageCount !== undefined && (
              <Chip
                label={`Fake Images: ${fakeImageCount}`}
                color={fakeImageCount > 0 ? "error" : "success"}
                sx={{ fontWeight: "500", fontSize: "0.875rem" }}
              />
            )}
            {realRate && (
              <Chip
                label={`Real Rate: ${realRate}`}
                color="success"
                sx={{ fontWeight: "500", fontSize: "0.875rem" }}
              />
            )}
            {fakeRate && (
              <Chip
                label={`Fake Rate: ${fakeRate}`}
                color="error"
                sx={{ fontWeight: "500", fontSize: "0.875rem" }}
              />
            )}
          </Box>
 
          <Box sx={{
            p: 3,
            bgcolor: "#f0f8ff",
            borderRadius: 2,
            borderLeft: "4px solid #1976d2"
          }}>
            <Typography variant="body1" color="text.secondary">
              <strong>Analysis Summary:</strong> This comprehensive biometric analysis combines
              behavioral detection (head movement, blink, smile, depth) with frame-by-frame liveness
              analysis to determine authenticity. The composite confidence score provides the final assessment.
            </Typography>
          </Box>
        </Box>
 
        {/* Captured Artifacts */}
        <Box sx={{
          bgcolor: "white",
          p: 3,
          borderRadius: 3,
          mb: 3,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
        }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom color="#1976d2" sx={{ mb: 3 }}>
            Captured Artifacts
          </Typography>
          <Grid container spacing={3}>
            {[
              {
                label: "PAN Card Image",
                img: mediaData?.digilocker_pan_presigned_url || "https://via.placeholder.com/250x150/1976d2/ffffff?text=PAN+Card",
                color: "#1976d2",
                available: !!mediaData?.digilocker_pan_presigned_url
              },
              {
                label: "Aadhaar Image",
                img: digilockerData?.aadhaar_image_s3_key || digilockerData?.aadhaar_photo_url || mediaData?.digilocker_aadhaar_presigned_url || "https://via.placeholder.com/250x150/4caf50/ffffff?text=Aadhaar",
                color: "#4caf50",
                available: !!(digilockerData?.aadhaar_image_s3_key || digilockerData?.aadhaar_photo_url || mediaData?.digilocker_aadhaar_presigned_url)
              },
              {
                label: "Liveliness Video",
                img: mediaData?.digilocker_liveliness_presigned_url || "https://via.placeholder.com/250x150/ff9800/ffffff?text=Liveliness+Video",
                color: "#ff9800",
                available: !!mediaData?.digilocker_liveliness_presigned_url,
                isVideo: true
              },
            ].map((item) => (
              <Grid item xs={12} md={4} key={item.label}>
                <Box sx={{
                  textAlign: "center",
                  p: 2,
                  border: `2px solid ${item.color}20`,
                  borderRadius: 3,
                  bgcolor: `${item.color}05`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: `0 4px 12px ${item.color}30`,
                    transform: "translateY(-2px)"
                  }
                }}>
                  {item.isVideo ? (
                    <video
                      src={item.img}
                      controls
                      style={{
                        width: "100%",
                        borderRadius: "12px",
                        maxHeight: "150px",
                        objectFit: "cover"
                      }}
                    />
                  ) : (
                    <img
                      src={item.img}
                      alt={item.label}
                      crossOrigin="anonymous"
                      style={{
                        width: "100%",
                        borderRadius: "12px",
                        maxHeight: "150px",
                        objectFit: "cover"
                      }}
                    />
                  )}
                  <Typography
                    variant="body1"
                    fontWeight="600"
                    sx={{ mt: 2, color: item.color }}
                  >
                    {item.label} {!item.available && "(Not Available)"}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
 
        {/* Auditor Actions */}
        <Box sx={{
          bgcolor: "white",
          p: 4,
          borderRadius: 3,
          textAlign: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
        }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom color="#1976d2" sx={{ mb: 3 }}>
            Auditor Actions
          </Typography>
         
          {sessionData?.auditor_status === "approved" ? (
            <Box sx={{
              p: 3,
              bgcolor: "#e8f5e8",
              borderRadius: 2,
              border: "2px solid #4caf50"
            }}>
              <Typography variant="h6" color="#2e7d32" fontWeight="bold">
                ✓ Already Approved
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This KYC session has been approved
              </Typography>
            </Box>
          ) : sessionData?.auditor_status === "rejected" ? (
            <Box sx={{
              p: 3,
              bgcolor: "#ffebee",
              borderRadius: 2,
              border: "2px solid #f44336"
            }}>
              <Typography variant="h6" color="#c62828" fontWeight="bold">
                ✗ Already Rejected
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This KYC session has been rejected
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", gap: 3, justifyContent: "center" }}>
              <Button
                variant="outlined"
                color="error"
                size="large"
                onClick={() => handleDecline(user)}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : null}
                sx={{
                  px: 4,
                  py: 2,
                  fontWeight: "bold",
                  textTransform: "none",
                  fontSize: "1.1rem",
                  borderRadius: 3
                }}
              >
                Decline
              </Button>
              <Button
                variant="contained"
                color="success"
                size="large"
                onClick={() => handleAccept(user)}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : null}
                sx={{
                  px: 4,
                  py: 2,
                  fontWeight: "bold",
                  textTransform: "none",
                  fontSize: "1.1rem",
                  borderRadius: 3,
                  boxShadow: "0 4px 12px rgba(76,175,80,0.3)",
                  "&:hover": {
                    boxShadow: "0 6px 16px rgba(76,175,80,0.4)"
                  }
                }}
              >
                Accept
              </Button>
            </Box>
          )}
                  </Box>
      </DialogContent>
     

    </Dialog>
  );
};
 
export default KYCDetailsPopup;
 